import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { profileService } from '../services/profileService';
import { useMemo } from 'react';
import LoadingSpinner from './common/LoadingSpinner';

// Sub-components
import ProfileHeader from './profile/ProfileHeader';
import ProfileForm from './profile/ProfileForm';
import DangerZone from './profile/DangerZone';
import DeleteAccountModal from './profile/DeleteAccountModal';
import { useUser } from '../context/UserContext';

function Profile({ onLogout, onUserUpdate }) {
    const { user } = useUser();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [formData, setFormData] = useState({
        display_name: user?.displayName || '',
        job_title: '',
        phone_number: '',
        bio: ''
    });

    useEffect(() => {
        let isMounted = true;

        const fetchProfile = async () => {
            try {
                const data = await profileService.getProfile();
                if(isMounted) {
                    setProfile(data.user);
                    setFormData({
                        display_name: data.user.display_name || '',
                        job_title: data.user.job_title || '',
                        phone_number: data.user.phone_number || '',
                        bio: data.user.bio || ''
                    });
                }
            } catch (err) {
                console.error('Failed to fetch profile:', err);
                if(isMounted) {
                    toast.error('Failed to load profile data');
                }
            } finally {
                if(isMounted) {
                setLoading(false);
                }
            }
        };
        fetchProfile();
        return () => { isMounted = false; };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field when user starts typing
        if (fieldErrors[name]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setFieldErrors({}); // Clear previous errors
        
        try {
            // console.log('formData', formData);
            const result = await profileService.updateProfile(formData);
            if (result.user) {
                setProfile(result.user);
                onUserUpdate(result.user);
                setFieldErrors({}); // Clear errors on success
                toast.success('Profile updated successfully');
            }
        } catch (err) {
            console.error('Update failed:', err);

            const status = err.response?.status;
            const errorData = err.response?.data;
          
            if (status === 422) {
                // 422 ValidationError or PersistenceError: validation/persistence failure for updates
                if (errorData?.errors) {
                    // ValidationError: has field-specific errors for display in form
                    setFieldErrors(errorData.errors);
                    toast.error(errorData.error || 'Validation failed');
                } else {
                    // PersistenceError: unexpected save failure (no external side effects)
                    toast.error(errorData?.error || 'Failed to save changes');
                }
            } else if (status === 502 && errorData?.error) {
                // 502 EntraSyncError: external service (Entra) integration failure
                toast.error(errorData.error);
            } else {
                toast.error('Failed to update profile. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteAccount = async () => {
        try {
            setIsDeleting(true); // Disable button, show loading indicator

            await profileService.deleteAccount(); // 204 No Content on success

            toast.success('Account deleted successfully');
            setShowDeleteModal(false); // Close modal ONLY on success
            onLogout();

        } catch (err) {
            console.error('Deletion failed:', err);
            const status = err.response?.status;
            const errorMsg = err.response?.data?.error;
        
            if (status === 403 && errorMsg) {
                // 403 AdministrativeRoleError: cannot delete account with admin roles
                toast.error(errorMsg);
            } else if (status === 500 && errorMsg) {
                // 500 PartialDeletionError: critical data inconsistency (Entra deleted, local deletion failed)
                toast.error(errorMsg);
            } else if (status === 502 && errorMsg) {
                // 502 EntraSyncError: external service (Entra) integration failure
                toast.error(errorMsg);
            } else {
                toast.error('Failed to delete account. Please try again.');
            }

        } finally {
            setIsDeleting(false); // Re-enable button regardless of success/failure
        }
    };

    // Calculate if form is dirty (fields changed)
    const isDirty = useMemo(() => {
        if (!profile) return false;
        
        return formData.display_name !== (profile.display_name || '') ||
               formData.job_title !== (profile.job_title || '') ||
               formData.phone_number !== (profile.phone_number || '') ||
               formData.bio !== (profile.bio || '');
    }, [formData, profile]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                <LoadingSpinner message="Loading..." />
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 font-sans p-6 transition-colors duration-300">
            <div className="max-w-4xl mx-auto py-8">
                <h1 className="text-3xl font-bold tracking-tight mb-10 text-center">Your Profile</h1>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column: Avatar and Info */}
                    <ProfileHeader
                        profile={profile}
                        user={user}
                    />

                    {/* Right Column: Edit Form and Danger Zone */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Edit Form */}
                        <ProfileForm
                            formData={formData}
                            onChange={handleChange}
                            onSubmit={handleSubmit}
                            saving={saving}
                            dirty={isDirty}
                            fieldErrors={fieldErrors}
                        />

                        {/* Danger Zone */}
                        <DangerZone
                            onShowDeleteModal={() => setShowDeleteModal(true)}
                        />
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <DeleteAccountModal
                show={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteAccount}
                isDeleting={isDeleting}
            />
        </div>
    );
}

export default Profile;
